import React, { useState, useEffect } from "react";
import { Bell, ExternalLink, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { getList, setList, getSavedSearches, deleteSavedSearch } from "@/lib/storage";
import { companyLogo, companyInitials } from "@/lib/companyLogo";

function useStored(kind, evtPredicate = () => true) {
  const [items, setItems] = useState(getList(kind));
  useEffect(() => {
    const handler = (e) => {
      if (evtPredicate(e)) setItems(getList(kind));
    };
    window.addEventListener("jh:storage", handler);
    return () => window.removeEventListener("jh:storage", handler);
  }, [kind]);
  return [items, setItems];
}

export default function DashboardAlerts() {
  const [jobs] = useStored("alerts");
  const [searches, setSearches] = useState(getSavedSearches());

  useEffect(() => {
    const h = () => setSearches(getSavedSearches());
    window.addEventListener("jh:storage", h);
    return () => window.removeEventListener("jh:storage", h);
  }, []);

  const removeJob = (url) => setList("alerts", getList("alerts").filter((j) => j.url !== url));

  return (
    <div data-testid="dash-alerts" className="space-y-6">
      <div>
        <h1 className="font-display text-3xl md:text-4xl flex items-center gap-3">
          <Bell className="text-[#E10600]" /> JOB ALERTS
        </h1>
        <p className="font-mono-body text-sm text-[#0a0a0a]/65 mt-1.5 max-w-xl">
          Two ways to get pinged: alert a specific role, or save a whole search and get a digest when new matches drop.
        </p>
      </div>

      <div className="bg-white border-4 border-[#0a0a0a] shadow-brutal">
        <div className="px-4 md:px-5 py-3 bg-[#0a0a0a] text-[#FFC400] font-accent text-[10px] flex justify-between">
          <span>ROLE ALERTS</span>
          <span className="text-[#FDFBF7]/55">{jobs.length} active</span>
        </div>
        {jobs.length === 0 ? (
          <div className="px-6 py-8 text-center font-mono-body text-sm text-[#0a0a0a]/65">
            Hit the bell on any job in the <Link to="/dashboard/jobs" className="underline">Job Directory</Link> to track it.
          </div>
        ) : (
          jobs.map((j) => (
            <div key={j.url} className="grid grid-cols-12 gap-3 items-center px-4 md:px-5 py-3 border-b-2 border-[#0a0a0a]/10 last:border-b-0">
              <div className="col-span-12 md:col-span-7 flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 bg-[#FDFBF7] border-2 border-[#0a0a0a] flex items-center justify-center overflow-hidden">
                  <img src={companyLogo(j.company) || ""} alt="" className="w-full h-full object-contain" onError={(e) => (e.target.style.display = "none")} />
                  <span className="font-display text-[10px] absolute">{companyInitials(j.company)}</span>
                </div>
                <div className="min-w-0">
                  <div className="font-display text-[14px] leading-tight truncate">{j.title}</div>
                  <div className="font-mono-body text-[11px] text-[#0a0a0a]/65">{j.company} · {j.location || "—"}</div>
                </div>
              </div>
              <div className="col-span-6 md:col-span-2 font-mono-body text-[11px] text-[#0a0a0a]/65">
                {j.alertAt ? new Date(j.alertAt).toLocaleDateString() : ""}
              </div>
              <div className="col-span-6 md:col-span-3 flex items-center justify-end gap-1.5">
                <button onClick={() => removeJob(j.url)} aria-label="Remove" className="hover-pop w-9 h-9 border-2 border-[#0a0a0a] bg-white inline-flex items-center justify-center shadow-brutal-sm">
                  <Trash2 size={13} strokeWidth={3} />
                </button>
                <a href={j.url} target="_blank" rel="noopener noreferrer" className="hover-pop bg-[#E10600] text-white border-2 border-[#0a0a0a] px-3 py-2 font-accent text-[11px] shadow-brutal-sm inline-flex items-center gap-1.5">
                  Apply <ExternalLink size={11} strokeWidth={3} />
                </a>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="bg-white border-4 border-[#0a0a0a] shadow-brutal">
        <div className="px-4 md:px-5 py-3 bg-[#0a0a0a] text-[#FFC400] font-accent text-[10px] flex justify-between">
          <span>SAVED SEARCH DIGESTS</span>
          <span className="text-[#FDFBF7]/55">{searches.length} saved</span>
        </div>
        {searches.length === 0 ? (
          <div className="px-6 py-8 text-center font-mono-body text-sm text-[#0a0a0a]/65">
            Save a query from the Job Directory to get pinged when new matches go live.
          </div>
        ) : (
          searches.map((s) => (
            <div key={s.id} className="flex items-center justify-between px-4 md:px-5 py-3 border-b-2 border-[#0a0a0a]/10 last:border-b-0">
              <div className="min-w-0">
                <div className="font-mono-body text-[13px] truncate">{s.label}</div>
                <div className="font-mono-body text-[10px] text-[#0a0a0a]/55 truncate">
                  {Object.entries(s.params).filter(([, v]) => v).map(([k, v]) => `${k}=${v}`).join(" · ")}
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <Link
                  to={`/dashboard/jobs?${new URLSearchParams(s.params).toString()}`}
                  className="hover-pop bg-white border-2 border-[#0a0a0a] px-2.5 py-1.5 font-accent text-[11px] shadow-brutal-sm"
                >
                  Run
                </Link>
                <button
                  onClick={() => {
                    deleteSavedSearch(s.id);
                    setSearches(getSavedSearches());
                  }}
                  aria-label="Delete"
                  className="hover-pop w-9 h-9 border-2 border-[#0a0a0a] bg-white inline-flex items-center justify-center shadow-brutal-sm"
                >
                  <Trash2 size={13} strokeWidth={3} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
